import { useState } from "react";

import { nodeStateMove } from "@/api/workflow";
import { config } from "@/config/base";
import { getToken } from "@/auth";
import { jwtDecode } from "jwt-decode";
import { useLocation, useNavigate } from "react-router-dom";
import MoonLoader from "react-spinners/MoonLoader";

import { useArticle, useAuthorArticle } from "@/hooks/server-state/use-article";

const Loading = () => {
  return (
    <div className="flex h-screen w-full items-center justify-center bg-white">
      <MoonLoader color="#0099A5" size={50} />
    </div>
  );
};

const useLastNumberFromUrl = () => {
  const location = useLocation();
  const path = location.pathname; // مثلاً: /article/12345
  const lastSegment = path.substring(path.lastIndexOf("/") + 1);
  return /^\d+$/.test(lastSegment) ? lastSegment : null;
};

const ArticleDetailPage = () => {
  const { accessToken } = getToken();
  const userId = jwtDecode<{ UserId: string }>(accessToken!).UserId;

  const lastNumber = useLastNumberFromUrl();

  const { data: ArticleType, isLoading: isArticleTypeLoading } = useArticle(
    lastNumber ?? ""
  );

  const { data: AuthorArticleType, isLoading: isAuthorArticleTypeLoading } =
    useAuthorArticle(
      Number(userId),
      ArticleType?.id ?? 0 // مقدار 0 باعث میشه تا وقتی داده نیومده، query اجرا نشه
    );

  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);

  const words = ArticleType?.abstract?.split(" ") || [];
  const isLong = words.length > 60;
  const displayedText = isExpanded
    ? words.join(" ")
    : words.slice(0, 60).join(" ") + (isLong ? "..." : "");

  const isLoading = isArticleTypeLoading;
  if (isLoading) return <Loading />;

  const isLoading2 = isAuthorArticleTypeLoading;
  if (isLoading2) return <Loading />;

  const handleGoBack = () => {
    navigate(-1);
  };

  const NavigateToEditPage = () => {
    navigate("/dashboard/article/edit/" + lastNumber);
  };

  const NavigateToTicketPage = () => {
    navigate("/dashboard/ticket/" + lastNumber);
  };

  const navigateToRemovePage = async () => {
    try {
      const data = await nodeStateMove(
        AuthorArticleType?.workflowUserId ?? "",
        4,
        "IQuyZMh8jk4bg5HWHcrGV",
        null
      );
      if (data?.data) {
        if (data.data !== (AuthorArticleType?.workflowUserId ?? "")) {
          navigate(`/dashboard/workflow-form/${data.data}`, {
            state: {
              tabTitle: "فرم گردش کار",
              tabPath: `/dashboard/workflow-form/${data.data}`
            }
          });
        } else {
          navigate(`/dashboard/workflow-form/${data.data}`);
        }
      }
    } catch (error) {
      console.error("خطا در تغییر وضعیت:", error);
    }
  };

  const navigateToacceptPage = async () => {
    try {
      const data = await nodeStateMove(
        AuthorArticleType?.workflowUserId ?? "",
        4,
        "x_psJIKpNMpZ9TglmSKLj",
        null
      );
      if (data?.data) {
        if (data.data !== (AuthorArticleType?.workflowUserId ?? "")) {
          navigate(`/dashboard/workflow-form/${data.data}`, {
            state: {
              tabTitle: "فرم گردش کار",
              tabPath: `/dashboard/workflow-form/${data.data}`
            }
          });
        } else {
          navigate(`/dashboard/workflow-form/${data.data}`);
        }
      }
    } catch (error) {
      console.error("خطا در تغییر وضعیت:", error);
    }
  };

  const navigateToFinalAcceptPage = async () => {
    try {
      const data = await nodeStateMove(
        AuthorArticleType?.workflowUserId ?? "",
        4,
        "x_psJIKpNMpZ9TglmSKLj",
        null
      );
      if (data?.data) {
        if (data.data !== (AuthorArticleType?.workflowUserId ?? "")) {
          navigate(`/dashboard/workflow-form/${data.data}`, {
            state: {
              tabTitle: "فرم گردش کار",
              tabPath: `/dashboard/workflow-form/${data.data}`
            }
          });
        } else {
          navigate(`/dashboard/workflow-form/${data.data}`);
        }
      }
    } catch (error) {
      console.error("خطا در تغییر وضعیت:", error);
    }
  };

  return (
    <>
      <div className="mx-8 mt-8 bg-gray-50 px-8 py-8">
        <div>
          <div>
            <span className="mx-4 text-2xl">مشخصات مقاله</span>
          </div>

          <div className="mx-8 mt-10 flex">
            <p className="text-base text-gray-500">شماره مقاله: </p>
            <p className="mx-2 text-base font-bold">{ArticleType?.doi}</p>
          </div>

          <div className="mx-8 mt-10 flex flex-nowrap">
            <p className="min-w-20 text-base text-gray-500">عنوان مقاله:</p>
            <p className="mx-2 text-base font-bold">{ArticleType?.title}</p>
          </div>

          <div className="mx-8 mt-10 flex flex-nowrap">
            <p className="min-w-20 text-base text-gray-500">عنوان مجله:</p>
            <p className="mx-2 text-base font-bold text-blue-800">
              {ArticleType?.journal?.title}
            </p>
            <p className="mx-2 text-base font-bold text-gray-500">
              (
              {ArticleType?.pageEnd != undefined
                ? "صفحه :" + ArticleType?.pageEnd + "-" + ArticleType?.pageStart
                : ""}{" "}
              {ArticleType?.publication != undefined
                ? "- سال " + ArticleType?.publication
                : ""}{" "}
              {ArticleType?.volume != undefined
                ? "- شماره" + ArticleType?.volume
                : ""}{" "}
              {ArticleType?.issue != undefined
                ? "- دوره" + ArticleType?.issue
                : ""}
              )
            </p>
          </div>

          <div className="mx-8 mt-10 flex flex-wrap">
            <div className="flex flex-nowrap">
              <p className="w-32 text-base text-gray-500">تاریخ پذیرش مقاله:</p>
              <p className="mx-2 text-base font-bold">
                {ArticleType?.publication}
              </p>
            </div>

            <div className="mx-8 border border-y-0 border-r-0 border-gray-700"></div>

            <div className="mx-4 flex flex-nowrap">
              <p className="w-32 text-base text-gray-500">نمایه شده در:</p>
              <p className="mx-2 text-base font-bold text-blue-800">Scopus</p>
            </div>

            <div className="mx-8 border border-y-0 border-r-0 border-gray-700"></div>

            <div className="mx-4 flex flex-nowrap">
              <p className="text-base text-gray-500">ISSN:</p>
              <p className="mx-2 text-base font-bold">
                {ArticleType?.journal?.issn}{" "}
                {ArticleType?.journal?.eIssn != undefined
                  ? "- " + ArticleType?.journal?.eIssn
                  : ""}
              </p>
            </div>

            <div className="mx-8 border border-y-0 border-r-0 border-gray-700"></div>

            <div className="mx-4 flex flex-nowrap">
              <p className="text-base text-gray-500">زبان:</p>
              <p className="mx-2 text-base font-bold">
                {ArticleType?.originalLanguage}{" "}
              </p>
            </div>
          </div>
          <div className="mx-8 mt-10">
            <p className="w-96 text-base text-gray-500">چکیده مقاله:</p>
            <p className="mx-2 text-base font-bold">{displayedText}</p>
            {isLong && (
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="mx-2 mt-2 text-sm text-blue-500"
              >
                {isExpanded ? "کمتر" : "بیشتر"}
              </button>
            )}
          </div>
        </div>
        <div className="mt-14 px-14">
          <div className="flex">
            <p className="text-base text-gray-500">نویسندگان مقاله:</p>
          </div>

          <div className="flex h-10 w-full items-center justify-between border border-2 border-slate-200 px-4 shadow-xl">
            <td className="w-2 text-slate-400">ردیف</td>
            <td className="w-64 text-center text-slate-400">نام نویسنده</td>
            <td className="w-64 text-center text-slate-400">سمت نویسنده</td>
            <td className="w-64 text-center text-slate-400">وابستگی سازمانی</td>
            <td className="w-24 text-center text-slate-400">اعلام وابستگی</td>
          </div>
          {ArticleType?.authors?.map((item: any, index: number) => (
            <div className="mt-2 flex h-10 w-full items-center justify-between border border-slate-300 bg-gray-100 px-4">
              <td className="w-2">{index + 1}</td>
              <td className="w-64 text-center">{item.name}</td>
              <td className="w-64 text-center">
                {item.isCoAutor == true
                  ? config.appConfig.texts.internalMemberFull
                  : config.appConfig.texts.externalMember}
              </td>
              <td className="w-64 text-center">
                {item.affiliation != ""
                  ? item.affiliation
                  : config.appConfig.texts.externalMemberFull}
              </td>
              <td className="w-24 text-center">
                {item.isCoAutor == true ? "بله" : "خیر"}
              </td>
            </div>
          ))}
        </div>
        <div className="mb-14 mt-14 flex justify-between gap-4 px-14">
          <div className="flex w-full gap-4">
            <div className="w-1/2 text-center">
              <span className="text-2xl">مستخرج از</span>
              <div className="border-x- mt-4 w-full border-b-2 border-t-0 border-blue-900"></div>

              <div className="mt-4">
                <div className="flex w-full justify-between px-2">
                  <p className="m-2 p-2">پارسا</p>
                  <p className="m-2 bg-gray-200 p-2">در دست پردازش</p>
                </div>

                <div className="flex w-full justify-between px-2">
                  <p className="mx-2 px-2 text-sm text-gray-400">
                    پایان نامه شماره : .........
                  </p>
                </div>
              </div>
              <div className="mt-4 border-x-0 border-b-2 border-t-0 border-slate-300"></div>

              <div>
                <div className="flex w-full justify-between px-2">
                  <p className="m-2 p-2">کتاب</p>
                  <p className="m-2 bg-gray-200 p-2">در دست پردازش</p>
                </div>
              </div>
              <div className="mt-4 border-x-0 border-b-2 border-t-0 border-slate-300"></div>

              <div>
                <div className="flex w-full justify-between px-2">
                  <p className="m-2 p-2">طرح پژوهشی</p>
                  <p className="m-2 bg-gray-200 p-2">در دست پردازش</p>
                </div>

                <div className="flex w-full justify-between px-2">
                  <p className="mx-2 px-2 text-sm text-gray-400">
                    {" "}
                    طرح پژوهشی شماره : .........
                  </p>
                </div>
              </div>
              <div className="mt-4 border-x-0 border-b-2 border-t-0 border-slate-300"></div>

              <div>
                <div className="flex w-full justify-between">
                  <p className="w- m-2 p-2 text-end">ماموریت پژوهشی</p>
                  <p className="m-2 w-32 bg-gray-200 p-2">در دست پردازش</p>
                </div>

                <div className="flex w-full justify-between px-2">
                  <p className="mx-2 px-2 text-sm text-gray-400">
                    ماموریت پژوهشی شماره : .........
                  </p>
                </div>
              </div>
              <div className="mt-4 border-x-0 border-b-2 border-t-0 border-slate-300"></div>

              <div>
                <div className="flex w-full justify-between px-2">
                  <p className="m-2 p-2">گرنت پژوهشی</p>
                  <p className="m-2 bg-gray-200 p-2">در دست پردازش</p>
                </div>

                <div className="flex w-full justify-between px-2">
                  <p className="mx-2 px-2 text-sm text-gray-400">
                    گرنت پژوهشی شماره : .........
                  </p>
                </div>
              </div>
              <div className="mt-4 border-x-0 border-b-2 border-t-0 border-slate-300"></div>
              <div>
                <div className="flex w-full justify-between px-2">
                  <p className="m-2 p-2">اختراع و اکتشاف</p>
                  <p className="m-2 bg-gray-200 p-2">در دست پردازش</p>
                </div>

                <div className="flex w-full justify-between px-2">
                  <p className="mx-2 px-2 text-sm text-gray-400">
                    {" "}
                    اختراع و اکتشاف شماره : .........
                  </p>
                </div>
              </div>
            </div>

            <div className="border-y-0 border-l-2 border-r-0 border-slate-300"></div>

            <div className="w-1/2 text-center">
              <span className="text-2xl">شاخص ها</span>
              <div className="border-x- mt-4 w-full border-b-2 border-t-0 border-blue-900"></div>

              <div className="mt-4">
                <div className="flex w-full justify-between px-2">
                  <p className="m-2 p-2">نوع انتشار :</p>
                  <p className="m-2 p-2">
                    {ArticleType?.doi != undefined
                      ? "منتشر شده (DOI)"
                      : "در دست انتشار"}
                  </p>
                </div>

                <div className="flex w-full justify-between px-2">
                  <p className="m-2 p-2">گروه علمی:</p>
                  <p className="m-2 bg-gray-200 p-2">در دست پردازش</p>
                </div>

                <div className="flex w-full justify-between px-2">
                  <p className="m-2 p-2">نوع مقاله :</p>
                  <p className="m-2 p-2">{ArticleType?.type}</p>
                </div>

                <div className="flex w-full justify-between px-2">
                  <p className="m-2 p-2">زمینه تحقیقاتی :</p>
                  <p className="m-2 bg-gray-200 p-2">در دست پردازش</p>
                </div>

                <div className="flex w-full justify-between px-2">
                  <p className="m-2 p-2">رویکرد اسلامی :</p>
                  <p className="m-2 bg-gray-200 p-2">در دست پردازش</p>
                </div>

                <div className="flex w-full justify-between px-2">
                  <p className="m-2 p-2">میزان همپوشانی :</p>
                  <p className="m-2 bg-gray-200 p-2">در دست پردازش</p>
                </div>

                <div className="flex w-full justify-between px-2">
                  <p className="m-2 p-2">درصد همپوشانی :</p>
                  <p className="m-2 bg-gray-200 p-2">در دست پردازش</p>
                </div>
              </div>
            </div>
          </div>
          <div className="border-y-0 border-l-2 border-r-0 border-slate-300"></div>

          {/* <div className="w-1/3 text-center">
            <span className="text-2xl">پایگاه های نمایه شده</span>
            <div className="w-full border-x- border-t-0 border-b-2 border-blue-900 mt-4"></div>

            <div className="mt-14">
              <div className="flex justify-end">
                <div className="flex">
                  <p className="text-gray-400">sjr :</p>
                  <p className="text-gray-900"></p>
                </div>

                <div className="flex mx-8">
                  <p className="text-gray-400">JCI :</p>
                  <p className="text-gray-900">147852</p>
                </div>

                <div className="flex mx-8">
                  <p className="text-gray-400">MIF :</p>
                  <p className="text-gray-900">147852</p>
                </div>
              </div>

              <div className="w-full flex">
                <div className="w-full">
                  <div className="bg-gray-50 w-full px-4 flex justify-between h-10 items-center border border-slate-200 border-2 shadow-xl">
                    <td className="w-14">Category</td>
                    <td className="w-14 text-center">Qurtile</td>
                    <td className="w-14 text-center">Rank</td>
                    <td className="w-14 text-center">Percentile</td>
                  </div>
                  <div className="shadow-xl">
                    <div className="w-full px-4 flex justify-between h-10 items-center border border-slate-300 bg-gray-100 mt-1">
                      <td className="w-14">0</td>
                      <td className="w-14 text-center">0</td>
                      <td className="w-14 text-center">0</td>
                      <td className="w-14 text-center">0</td>
                      <td className="w-14 text-center">0 </td>
                      <td className="w-14 text-center">0</td>
                    </div>
                    <div className="w-full px-4 flex justify-between h-10 items-center border border-slate-300 bg-gray-200">
                      <td className="w-14">0</td>
                      <td className="w-14 text-center">0</td>
                      <td className="w-14 text-center">0</td>
                      <td className="w-14 text-center">0</td>
                      <td className="w-14 text-center">0</td>
                      <td className="w-14 text-center">0</td>
                    </div>
                  </div>
                </div>
                <div className="px-2 bg-gray-50 border border-slate-200 border-2 shadow-xl flex items-center">ISI</div>
              </div>
            </div>
          </div> */}
        </div>
        <div className="border-bottem-0 border-black-900 absolute bottom-0 left-4 rounded border-2 bg-gray-100 py-2 text-center">
          <button
            className={
              "mx-2 mt-2 w-20 rounded-md bg-gray-200 px-2 py-2" +
              (AuthorArticleType?.status === "در انتظار تایید نهایی" &&
                " bg-primary")
            }
            onClick={navigateToFinalAcceptPage}
            disabled={AuthorArticleType?.status !== "در انتظار تایید نهایی"}
          >
            تایید نهایی
          </button>

          <button
            className={
              "w-26 mx-2 mt-2 rounded-md bg-gray-200 px-2 py-2" +
              (AuthorArticleType?.status === "در انتظار تایید استاد" &&
                " bg-primary")
            }
            onClick={navigateToacceptPage}
            disabled={AuthorArticleType?.status === "در انتظار تایید نهایی"}
          >
            تایید مالکیت
          </button>

          <button
            className={
              "mx-2 mt-2 w-20 rounded-md bg-gray-200 px-2 py-2" +
              (AuthorArticleType?.status === "در انتظار تایید نهایی" &&
                " bg-yellow-500")
            }
            onClick={NavigateToEditPage}
            disabled={AuthorArticleType?.status !== "در انتظار تایید نهایی"}
          >
            ویرایش
          </button>

          <button
            className={
              "mx-2 mt-2 w-20 rounded-md bg-gray-200 px-2 py-2" +
              (AuthorArticleType?.status === "در انتظار تایید نهایی" &&
                " bg-yellow-500")
            }
            onClick={NavigateToTicketPage}
            disabled={AuthorArticleType?.status !== "در انتظار تایید نهایی"}
          >
            درخواست
          </button>

          <button
            className="mx-2 mt-2 w-20 rounded-md bg-red-400 px-2 py-2"
            onClick={navigateToRemovePage}
          >
            حذف
          </button>

          <button
            className="mx-2 mt-2 w-20 rounded-md bg-red-400 px-2 py-2"
            onClick={handleGoBack}
          >
            بازگشت
          </button>
        </div>
      </div>
    </>
  );
};

export default ArticleDetailPage;
